import React, { useCallback, useEffect, useRef, useState } from "react";
import { Dialog, useMediaQuery } from "@material-ui/core";
import { useSelector } from "react-redux";
import { usePostHog } from "posthog-js/react";
import { isMobile } from "react-device-detect";
import styles from "./MyWashPromoV2.module.scss";
import mywashPromoService from "../../../apis/services/mywashPromo/mywashPromoService";
import {
  readPromoState,
  canAutoOpen,
  recordAutoOpen,
  recordNever,
} from "./mywashPromoState";

// MyWashPromoV2 is a thin wrapper around a pre-built, already-approved
// static bundle (public/mywash-promo/v2/*) shown in two same-origin
// iframes: a small "tile" on the dashboard and a pop-up dialog it opens.
// This component owns: fetching the logged-in user's own wash from the
// backend (GET /mywash-promo/me - already filtered server-side, never a
// client list in a public file), sizing/opening/closing the dialog,
// validating postMessage traffic from the iframes, auto-open frequency,
// and PostHog analytics. It does not own any visual design - that lives
// entirely in the bundle.

const PROMO_BASE = `${process.env.PUBLIC_URL || ""}/mywash-promo/v2`;
const TILE_URL = `${PROMO_BASE}/tile.html`;
const DIALOG_URL = `${PROMO_BASE}/index.html`;
const REGISTER_URL = "https://app.mywash.rs/partner-registration";
const FRAME_TITLE = "MyWash za Vašu perionicu";
const CAMPAIGN = "mywash_v3_personal";
const MESSAGE_PREFIX = "mywashpromo:";

const MIN_TILE_HEIGHT = 72;
const MAX_TILE_HEIGHT = 200;
const DEFAULT_TILE_HEIGHT = 92;

// Below this width the dialog goes fullScreen (matches the CSS breakpoint
// below). Kept as plain px, not rem: media queries resolve em/rem against
// the browser's default root font size, not this page's 62.5% override, so
// px is the only unit that can't silently drift out of sync with the JS
// check here.
const FULLSCREEN_BREAKPOINT_QUERY = "(max-width:899px)";

// The frame URL never carries the wash - only "embed" (plus "demo=<key>"
// for local preview outside SDF, not used here). The wash itself travels
// only in the in-memory "config" message, same as registerUrl/ownerName.
const FRAME_SRC = (baseUrl) => `${baseUrl}?embed`;

// Fields the ad bundle actually renders; anything else on the backend's
// wash object (there shouldn't be any) is dropped here too, defensively.
const WASH_FIELDS = [
  "name",
  "address",
  "city",
  "color",
  "color2",
  "gradDir",
  "textColor",
  "accentColor",
  "logoTint",
  "monogram",
  "cardLogoScale",
  "screen",
  "logo",
  "cardLogo",
  "photo",
  "hero",
];

const sanitizeWash = (wash) => {
  if (!wash || typeof wash !== "object") return null;
  const out = {};
  WASH_FIELDS.forEach((field) => {
    if (Object.prototype.hasOwnProperty.call(wash, field)) out[field] = wash[field];
  });
  return out;
};

// Ported as-is from the old MyWashAppPromoModal.js: prefill the partner
// registration form with the logged-in owner's company + contact data.
// Not part of this change - kept behaviourally identical on purpose.
const buildRegisterUrl = (user) => {
  const company = user?.companies?.[0];
  const contactPerson = `${user?.firstName ?? ""} ${user?.lastName ?? ""}`.trim();
  const params = new URLSearchParams();
  if (company?.name) params.set("companyName", company.name);
  if (company?.PIB) params.set("pib", company.PIB);
  if (contactPerson) params.set("contactPerson", contactPerson);
  if (user?.email) params.set("email", user.email);
  if (user?.phone) params.set("phone", user.phone);
  const qs = params.toString();
  return qs ? `${REGISTER_URL}?${qs}` : REGISTER_URL;
};

const clampTileHeight = (value) =>
  Math.max(MIN_TILE_HEIGHT, Math.min(MAX_TILE_HEIGHT, value));

const MyWashPromoV2 = () => {
  const user = useSelector((state) => state.auth.user);
  const userId = user?.id;
  const posthog = usePostHog();
  const fullScreen = useMediaQuery(FULLSCREEN_BREAKPOINT_QUERY);

  const [washResolved, setWashResolved] = useState(false);
  const [wash, setWash] = useState(null);
  const [washRef, setWashRef] = useState(null);
  const [isOpen, setIsOpen] = useState(false);
  const [tileHeight, setTileHeight] = useState(DEFAULT_TILE_HEIGHT);
  // From the dialog's own "ready" message when available; otherwise we fall
  // back to whether a wash was resolved (see trackEvent below).
  const [personalized, setPersonalized] = useState(null);

  const tileFrameRef = useRef(null);
  const dialogFrameRef = useRef(null);
  const openedAtRef = useRef(null);
  const autoOpenAttemptedRef = useRef(false);
  const tileViewedRef = useRef(false);

  // --- 1. Fetch the logged-in user's own wash from the backend, once.
  // Never throws (mywashPromoService swallows failures to null), so a
  // network/auth error just shows the neutral ad, same as "no wash". ---
  useEffect(() => {
    let cancelled = false;
    mywashPromoService.fetchMyWash().then((data) => {
      if (cancelled) return;
      setWash(sanitizeWash(data));
      // opaque id like "bu-46" from the backend, only to group analytics per wash
      setWashRef(typeof data?.ref === "string" && /^[a-z0-9-]{1,40}$/i.test(data.ref) ? data.ref : null);
      setWashResolved(true);
    });
    return () => {
      cancelled = true;
    };
  }, []);

  const trackEvent = useCallback(
    (eventName, extra = {}) => {
      posthog?.capture(eventName, {
        campaign: CAMPAIGN,
        personalized: personalized ?? Boolean(wash),
        ...(washRef ? { wash_ref: washRef } : {}),
        // SDF account id (number only, no name/e-mail): sdf.rs never identifies
        // users in PostHog, so without it a neutral ad could not be traced
        ...(userId != null ? { sdf_user_id: userId } : {}),
        ...extra,
      });
    },
    [posthog, personalized, wash, washRef, userId]
  );

  // Tile viewed: once per mount, after the wash fetch finishes either way.
  useEffect(() => {
    if (!washResolved || tileViewedRef.current) return;
    tileViewedRef.current = true;
    trackEvent("mywash_promo_tile_viewed");
    // trackEvent intentionally excluded: it would re-run this on every
    // wash/personalized change, but this event must fire exactly once.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [washResolved]);

  const returnFocusToTile = useCallback(() => {
    tileFrameRef.current?.focus();
  }, []);

  const openDialog = useCallback(
    (source) => {
      setIsOpen(true);
      openedAtRef.current = Date.now();
      trackEvent("mywash_promo_opened", {
        source,
        device_type: isMobile ? "mobile" : "desktop",
      });
    },
    [trackEvent]
  );

  const closeDialog = useCallback(
    (source) => {
      const durationSec = openedAtRef.current
        ? Math.round((Date.now() - openedAtRef.current) / 1000)
        : null;
      setIsOpen(false);
      trackEvent("mywash_promo_dismissed", {
        source,
        duration_seconds: durationSec,
      });
      returnFocusToTile();
    },
    [trackEvent, returnFocusToTile]
  );

  const dismissForever = useCallback(
    (source) => {
      if (userId) recordNever(userId);
      setIsOpen(false);
      trackEvent("mywash_promo_dont_show_again", source ? { source } : {});
      returnFocusToTile();
    },
    [userId, trackEvent, returnFocusToTile]
  );

  // --- Leaving without X: back button / navigating away unmounts this
  // component; switching app or locking the phone hides the page. Both used
  // to leave no trace, so time-on-ad was unknown for those owners. ---
  const isOpenRef = useRef(false);
  isOpenRef.current = isOpen;
  const trackEventRef = useRef(trackEvent);
  trackEventRef.current = trackEvent;
  const openSeconds = () =>
    openedAtRef.current ? Math.round((Date.now() - openedAtRef.current) / 1000) : null;
  useEffect(() => {
    const onHidden = () => {
      if (document.visibilityState === "hidden" && isOpenRef.current) {
        trackEventRef.current("mywash_promo_hidden", { duration_seconds: openSeconds() });
      }
    };
    document.addEventListener("visibilitychange", onHidden);
    return () => {
      document.removeEventListener("visibilitychange", onHidden);
      if (isOpenRef.current) {
        trackEventRef.current("mywash_promo_dismissed", {
          source: "left_page",
          duration_seconds: openSeconds(),
        });
      }
    };
  }, []);

  // --- Phone "back" closes the pop-up instead of leaving the page (it used
  // to send owners back to /login). Opening adds one history step on the
  // same URL, keeping the router's own state; "back" pops it and we close.
  // Closing any other way removes that step again. ---
  const historyStepRef = useRef(false);
  const closeDialogRef = useRef(closeDialog);
  closeDialogRef.current = closeDialog;
  useEffect(() => {
    if (!isOpen) return undefined;
    // a fresh token per opening, so a stale step left by an earlier opening
    // (owner navigated away with the pop-up open) is never mistaken for ours
    const token = `${Date.now()}-${Math.random()}`;
    window.history.pushState({ ...(window.history.state || {}), mywashPromo: token }, "");
    historyStepRef.current = true;
    const onPop = () => {
      if (!historyStepRef.current) return;
      historyStepRef.current = false;
      closeDialogRef.current("back_button");
    };
    window.addEventListener("popstate", onPop);
    return () => {
      window.removeEventListener("popstate", onPop);
      if (!historyStepRef.current) return;
      historyStepRef.current = false;
      // Only undo our own step; after navigating away the top entry is not ours.
      if (window.history.state?.mywashPromo === token) window.history.back();
    };
  }, [isOpen]);

  // --- 2. Auto-open, once the user id is known and the wash fetch settled.
  // Recorded immediately (not on close) so leaving the page without
  // pressing X still counts against the frequency cap. ---
  useEffect(() => {
    if (!washResolved || !userId || autoOpenAttemptedRef.current) return;
    autoOpenAttemptedRef.current = true;
    const now = Date.now();
    if (!canAutoOpen(readPromoState(userId), now)) return;
    recordAutoOpen(userId, now);
    openDialog("auto");
  }, [washResolved, userId, openDialog]);

  // --- 3. postMessage handling: same-origin only, and only from a frame
  // this component owns. Both frames may ask for "config" (wash +
  // registerUrl + ownerName); only the dialog may send close/never/cta. ---
  useEffect(() => {
    const onMessage = (event) => {
      if (event.origin !== window.location.origin) return;
      const data = event.data;
      if (
        !data ||
        typeof data.type !== "string" ||
        !data.type.startsWith(MESSAGE_PREFIX)
      ) {
        return;
      }
      const type = data.type.slice(MESSAGE_PREFIX.length);
      const fromTile =
        Boolean(tileFrameRef.current) &&
        event.source === tileFrameRef.current.contentWindow;
      const fromDialog =
        Boolean(dialogFrameRef.current) &&
        event.source === dialogFrameRef.current.contentWindow;

      if (fromTile && type === "tile" && Number.isFinite(data.height)) {
        setTileHeight(clampTileHeight(data.height));
        return;
      }
      if (fromTile && type === "open") {
        openDialog("banner_click");
        return;
      }

      const sendConfig = () => {
        // Hand the owner's data to the frame in memory, never in the frame
        // URL: the prefilled registration link (so its button can open the
        // new tab inside the user's own tap; a window.open from this async
        // handler is what Safari's pop-up blocker is most likely to
        // refuse), the first name shown in the phone mock-up's greeting,
        // and the wash itself (name/color/logo as a data: URI - never a
        // path the browser could request on its own).
        try {
          event.source.postMessage(
            {
              type: `${MESSAGE_PREFIX}config`,
              registerUrl: buildRegisterUrl(user),
              ownerName: typeof user?.firstName === "string" ? user.firstName : "",
              wash,
              // how many times it has opened by itself; from the 2nd time the
              // pop-up shows a quiet "Ne zanima me" right under the button
              autoViews: userId ? readPromoState(userId).autoCount : 0,
            },
            window.location.origin
          );
        } catch {
          // the pop-up then reports opened:false and we open it below
        }
      };

      if (fromTile && type === "hello") {
        sendConfig();
        return;
      }
      if (!fromDialog) return;
      if (type === "hello" || type === "ready") {
        if (type === "ready") setPersonalized(Boolean(data.personalized));
        // "hello" arrives before the pop-up draws, "ready" after; answering
        // both keeps the name/wash from popping in late.
        sendConfig();
      } else if (type === "close") {
        closeDialog(data.source === "escape" ? "escape" : "close_button");
      } else if (type === "seen") {
        // how far the owner read: numbers / reasons / end (sent once each)
        if (["brojevi", "razlozi", "kraj"].includes(data.section)) {
          trackEvent("mywash_promo_section_seen", { section: data.section });
        }
      } else if (type === "never") {
        dismissForever(data.source === "under_cta" ? "under_cta" : "footer");
      } else if (type === "cta") {
        if (data.action === "register") {
          // opened:true = the pop-up already opened the link itself
          if (data.opened !== true) {
            window.open(buildRegisterUrl(user), "_blank", "noopener,noreferrer");
          }
          trackEvent("mywash_promo_register_clicked", { source: "modal" });
        } else if (data.action === "call") {
          trackEvent("mywash_promo_call_clicked");
        }
      }
    };
    window.addEventListener("message", onMessage);
    return () => window.removeEventListener("message", onMessage);
  }, [openDialog, closeDialog, dismissForever, trackEvent, user, wash, userId]);

  return (
    <>
      {washResolved && (
        <iframe
          ref={tileFrameRef}
          title={FRAME_TITLE}
          scrolling="no"
          src={FRAME_SRC(TILE_URL)}
          className={styles.tileFrame}
          style={{ height: `${tileHeight}px` }}
        />
      )}

      <Dialog
        open={isOpen}
        onClose={() => closeDialog("backdrop_or_esc")}
        fullScreen={fullScreen}
        maxWidth={false}
        disableRestoreFocus
        classes={{ paper: styles.dialogPaper }}
        PaperProps={{ "aria-label": FRAME_TITLE }}
      >
        {isOpen && (
          <iframe
            ref={dialogFrameRef}
            title={FRAME_TITLE}
            src={FRAME_SRC(DIALOG_URL)}
            className={styles.dialogFrame}
          />
        )}
      </Dialog>
    </>
  );
};

export default MyWashPromoV2;
